import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useSyncExternalStore, useRef } from 'react';
import { QUERIES } from '../../utils/constants';
import { clientLogger } from '../../utils/logger';
import { LanguageProvider } from './Language';

interface LanguageProviderWrapperProps {
  children: ReactNode;
}

interface RetailerSettingsData {
  retailerSettings?: {
    translation?: string;
    name?: string;
    features?: {
      isOrderless?: boolean;
      isGiftLoginEnabled?: boolean;
    };
  };
}

/**
 * Wrapper component that fetches translations from retailer settings
 * and hydrates the LanguageProvider with them.
 */
export const LanguageProviderWrapper = ({
  children,
}: LanguageProviderWrapperProps) => {
  const queryClient = useQueryClient();
  const lastTranslationJSONRef = useRef<string | null>(null);

  const data = useSyncExternalStore(
    (callback) => {
      // Subscribe to query cache changes
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        // Only trigger re-render if retailer settings query changed
        // Check if the event is for our specific query by comparing the full queryKey
        const isRetailerSettingsQuery =
          event?.query?.queryKey &&
          Array.isArray(event.query.queryKey) &&
          event.query.queryKey.length > 0 &&
          event.query.queryKey[0] === QUERIES.GET_RETAILER_SETTINGS;

        if (!isRetailerSettingsQuery) return;

        // Read current JSON string and compare; only fire when it *actually* changes.
        const current = queryClient.getQueryData<RetailerSettingsData>([
          QUERIES.GET_RETAILER_SETTINGS,
        ]);
        const currentJSON = current?.retailerSettings?.translation ?? '';
        if (currentJSON !== lastTranslationJSONRef.current) {
          lastTranslationJSONRef.current = currentJSON;
          callback();
        }
      });
      return unsubscribe;
    },
    () => {
      const snap = queryClient.getQueryData<RetailerSettingsData>([
        QUERIES.GET_RETAILER_SETTINGS,
      ]);
      // keep ref in sync for the compare logic above
      const json = snap?.retailerSettings?.translation ?? '';
      if (json !== lastTranslationJSONRef.current)
        lastTranslationJSONRef.current = json;
      return snap;
    },
    // For SSR, prefer a deterministic snapshot to avoid hydration mismatch:
    () => ({ retailerSettings: { translation: '' } }),
  );

  // Parse translations from retailer settings (translations always come from server)
  const translations = (() => {
    if (!data?.retailerSettings?.translation) {
      return {};
    }

    try {
      return JSON.parse(data.retailerSettings.translation) as Record<
        string,
        string
      >;
    } catch (error) {
      clientLogger.logError(error);
      return {};
    }
  })();

  return (
    <LanguageProvider translations={translations}>{children}</LanguageProvider>
  );
};
